import { apiGet } from "./api";
import { getAdminProducts } from "./productService";
import { getAdminOrders } from "./orderService";
import { getAdminBlogs } from "./blogService";
import { getAdminMessages } from "./contactService";

function countResults(payload) {
  if (Array.isArray(payload)) return payload.length;
  if (Array.isArray(payload?.results)) return payload.results.length;
  if (typeof payload?.count === "number") return payload.count;
  if (Array.isArray(payload?.data)) return payload.data.length;
  return 0;
}

function normalizeResults(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

export async function getDashboardSummary() {
  const [products, orders, blogs, messages] = await Promise.all([
    getAdminProducts({ page: 1, page_size: 1 }).catch(() => ({ count: 0, results: [] })),
    getAdminOrders({ page: 1, page_size: 1 }).catch(() => ({ count: 0, results: [] })),
    getAdminBlogs({ page: 1, page_size: 1 }).catch(() => ({ count: 0, results: [] })),
    getAdminMessages().catch(() => ({ count: 0, results: [] })),
  ]);

  return {
    totalProducts: countResults(products),
    totalOrders: countResults(orders),
    totalBlogs: countResults(blogs),
    totalMessages: countResults(messages),
    products: normalizeResults(products),
    orders: normalizeResults(orders),
    blogs: normalizeResults(blogs),
    messages: normalizeResults(messages),
    raw: {
      products,
      orders,
      blogs,
      messages,
    },
  };
}

export async function getDashboardStats() {
  const summary = await getDashboardSummary();
  return {
    totalProducts: summary.totalProducts,
    totalOrders: summary.totalOrders,
    totalBlogs: summary.totalBlogs,
    totalMessages: summary.totalMessages,
  };
}

export async function getDashboardRecentActivity() {
  try {
    const messages = await getAdminMessages().catch(() => ({ results: [] }));
    const orders = await getAdminOrders({ page: 1, page_size: 10 }).catch(() => ({ results: [] }));
    const blogs = await getAdminBlogs({ page: 1, page_size: 10 }).catch(() => ({ results: [] }));

    return {
      messages: normalizeResults(messages),
      orders: normalizeResults(orders),
      blogs: normalizeResults(blogs),
    };
  } catch (error) {
    return {
      messages: [],
      orders: [],
      blogs: [],
    };
  }
}

export async function getDashboardInboxSummary() {
  try {
    const messages = await getAdminMessages().catch(() => ({ count: 0, results: [] }));
    const items = normalizeResults(messages);

    const unreadCount =
      typeof messages?.unread_count === "number"
        ? messages.unread_count
        : items.filter((item) => {
            const status = String(item?.status || "").toLowerCase();
            const isRead = item?.is_read === true;
            return !isRead && status !== "read" && status !== "resolved";
          }).length;

    return {
      total: countResults(messages),
      unread: unreadCount,
      items,
    };
  } catch (error) {
    return {
      total: 0,
      unread: 0,
      items: [],
    };
  }
}

export async function getDashboardOrdersSummary() {
  try {
    const orders = await getAdminOrders({ page: 1, page_size: 20 }).catch(() => ({ count: 0, results: [] }));
    return {
      total: countResults(orders),
      items: normalizeResults(orders),
    };
  } catch (error) {
    return {
      total: 0,
      items: [],
    };
  }
}

export async function getDashboardBlogsSummary() {
  try {
    const blogs = await getAdminBlogs({ page: 1, page_size: 20 }).catch(() => ({ count: 0, results: [] }));
    return {
      total: countResults(blogs),
      items: normalizeResults(blogs),
    };
  } catch (error) {
    return {
      total: 0,
      items: [],
    };
  }
}

export async function getDashboardProductsSummary() {
  try {
    const products = await getAdminProducts({ page: 1, page_size: 20 }).catch(() => ({ count: 0, results: [] }));
    return {
      total: countResults(products),
      items: normalizeResults(products),
    };
  } catch (error) {
    return {
      total: 0,
      items: [],
    };
  }
}

export async function pingDashboardHealth() {
  try {
    await apiGet("/site-settings/");
    return { ok: true };
  } catch (error) {
    return { ok: false, error: error?.message || "Dashboard health check gagal." };
  }
}